import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Megaphone, Plus, ShieldAlert } from "lucide-react";
import { fetchNotices } from "../../controllers/noticeController";
import { useNotices } from "./NoticeProvider";
import NoticeListItem from "./NoticeListItem";
import { isLive } from "./noticeUtils";

const LIMIT = 4;

/** Dashboard widget: pinned and latest live notices, with unread / acknowledgement counts. */
function NoticeBoardCard({ className = "" }) {
  const { version, unreadCount, pendingAckCount, canManage, openNotice, openEditor } = useNotices();
  const [state, setState] = useState({ loading: true, error: false, notices: [], total: 0 });

  useEffect(() => {
    let cancelled = false;
    fetchNotices({ limit: LIMIT })
      .then((data) => !cancelled && setState({ loading: false, error: false, notices: data.notices, total: data.total }))
      .catch(() => !cancelled && setState((s) => ({ ...s, loading: false, error: true })));
    return () => {
      cancelled = true;
    };
  }, [version]);

  const notices = state.notices.filter((n) => isLive(n));

  return (
    <section
      aria-label="Notice board"
      data-tour="notice-card"
      className={`flex h-full flex-col rounded-xl border border-app-primary bg-app-card p-5 shadow-sm ${className}`}
    >
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-sky-300">
            <Megaphone className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-app-heading">
              Notice Board
              {unreadCount > 0 && (
                <span className="rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-bold normal-case tracking-normal text-white">
                  {unreadCount} new
                </span>
              )}
            </h3>
            <p className="mt-0.5 text-xs text-app-muted">Announcements from HR and management.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {canManage && (
            <button
              type="button"
              onClick={() => openEditor()}
              className="inline-flex items-center gap-1 rounded-lg bg-[#2470B7] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#1f5f9b] dark:bg-blue-600 dark:hover:bg-blue-500"
            >
              <Plus className="h-3.5 w-3.5" /> Post
            </button>
          )}
          <Link
            to="/notices"
            className="group inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-sky-400 dark:hover:text-sky-300"
          >
            View all
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </header>

      {pendingAckCount > 0 && (
        <Link
          to="/notices?filter=pending-ack"
          className="mb-3 flex items-center gap-2 rounded-lg border border-app-warning bg-app-warning px-3 py-2 text-sm font-medium text-app-warning hover:opacity-90"
        >
          <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
          {pendingAckCount === 1 ? "1 notice needs" : `${pendingAckCount} notices need`} your acknowledgement
          <ArrowRight className="ml-auto h-4 w-4" aria-hidden="true" />
        </Link>
      )}

      <div className="grid min-h-0 flex-1 content-start gap-2.5 lg:grid-cols-2">
        {state.loading &&
          [0, 1, 2].map((i) => <div key={i} className="h-[92px] animate-pulse rounded-xl bg-app-hover" aria-hidden="true" />)}
        {!state.loading && state.error && <p className="py-6 text-center text-sm text-app-error lg:col-span-2">Couldn’t load notices.</p>}
        {!state.loading && !state.error && notices.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-2 py-8 text-center lg:col-span-2">
            <Megaphone className="h-8 w-8 text-app-muted opacity-60" aria-hidden="true" />
            <p className="text-sm text-app-muted">No notices right now.</p>
          </div>
        )}
        {!state.loading &&
          notices.map((notice) => <NoticeListItem key={notice.id} notice={notice} onOpen={openNotice} compact />)}
      </div>

      {!state.loading && state.total > LIMIT && (
        <Link to="/notices" className="mt-3 text-center text-xs font-semibold text-app-muted hover:text-app-primary">
          +{state.total - LIMIT} more on the notice board
        </Link>
      )}
    </section>
  );
}

export default NoticeBoardCard;
